import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, BookOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { Overline } from "../components/Overline";
import { Seo } from "../components/Seo";

export default function ForParents() {
  return (
    <div data-testid="page-for-parents" className="bg-[#faf9f6]">
      <Seo title="For Parents and Teens" description="Worried about your daughter? Acne, painful periods, irregular cycles and mood swings deserve more than a prescription. A real, gentle, root-cause path for young women." path="/for-parents" />
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-7">
            <Overline>For Parents & Teens</Overline>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.04] font-normal text-[#333] mt-5">
              Your daughter
              <br />
              deserves more than
              <br />
              <span className="italic">a prescription.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#3d3d3d] font-normal leading-relaxed max-w-2xl">
              Acne. Irregular periods. Anxiety. Painful cycles. Mood swings.
              Today, the standard answer offered to a 15 or 17 year old girl
              walking into a GP surgery is the same one offered to a 35 year
              old: the pill. No questions about her sleep, her stress, her
              nutrition, her gut. No conversation about what her cycle is
              actually telling her.
            </p>
            <p className="mt-6 text-lg text-[#3d3d3d] font-normal leading-relaxed max-w-2xl">
              We can do so much better than that.
            </p>
            <div className="mt-10">
              <a
                href="https://pay.gocardless.com/BRT01M17Y62WS2BX1QY0D8B0EPSXV"
                target="_blank"
                rel="noreferrer"
                data-testid="parents-cta-primary"
                className="inline-block"
              >
                <Button className="bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full px-8 py-6 h-auto text-base font-medium tracking-wide">
                  Book a parent consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <p className="mt-3 text-sm text-[#5a5a5a] font-normal">
                £45 · 30 minutes · guidance on whether we're a fit
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="arch-image aspect-[4/5] bg-[#cbdad6]">
              <img
                src="/brand/teens-acne.png"
                alt="Young woman in natural light"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#f3eee8]/60 border-y border-[#e5e0d8]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Overline>Why I do this work</Overline>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight font-normal text-[#333] mt-5">
            I want to stop a generation of girls
            <br />
            <span className="italic">being put blindly on the pill.</span>
          </h2>
          <div className="mt-10 space-y-6 text-lg text-[#3d3d3d] font-normal leading-relaxed">
            <p>
              The combined oral contraceptive pill is a powerful medication.
              For some women it is the right choice, knowingly and
              temporarily. But it is not the only choice, and it should never
              be the first one for a young woman whose cycle is still finding
              its rhythm.
            </p>
            <p>
              When a teenager goes on the pill at 14, 15 or 16, she stops
              ovulating. She stops producing her own oestrogen and
              progesterone. She trades a real cycle for a synthetic bleed.
              And she may stay on it for ten, fifteen, twenty years - never
              once meeting her own adult hormonal rhythm. When she eventually
              comes off, in her late twenties or early thirties, often to try
              for a baby, the very symptoms she was told the pill 'fixed'
              come roaring back, often worse.
            </p>
            <p>
              Worse, the pill can mask conditions like PCOS / PMOS for an
              entire reproductive lifetime. A girl with hormonal acne, weight
              changes and irregular periods at sixteen is not 'just hormonal'.
              She is communicating. Switching her bleed off with a synthetic
              hormone does not heal her, it silences her.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <Overline>What we actually do</Overline>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight font-normal text-[#333] mt-5 max-w-3xl">
          A real, gentle, root-cause path
          <br />
          <span className="italic">for young women.</span>
        </h2>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            {
              icon: ShieldCheck,
              title: "Find the real driver",
              body: "Acne, irregular periods, painful bleeds and mood swings have causes. Insulin, gut health, stress load, nutrient status, sleep. We look at all of it, gently, age-appropriately.",
            },
            {
              icon: BookOpen,
              title: "Body literacy from day one",
              body: "We teach your daughter to track her cycle properly, to read her own biomarkers, to understand her body as a story she is the author of. Not a problem to be medicated.",
            },
            {
              icon: Sparkles,
              title: "Real-food, real-life protocols",
              body: "No fads. No extremes. Foundational nutrition, circadian rhythm, nervous-system support and targeted nutrients. Built around a teenager's actual life, school, friends, busyness.",
            },
          ].map((c) => (
            <div
              key={c.title}
              data-testid={`parents-pillar-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="bg-white border border-[#e5e0d8] rounded-3xl p-8"
            >
              <c.icon className="text-[#5fa292]" size={28} />
              <h3 className="font-serif text-2xl text-[#333] mt-4 font-normal tracking-tight">
                {c.title}
              </h3>
              <p className="mt-3 text-[#3d3d3d] font-normal leading-relaxed">
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl p-12 md:p-16 text-center bg-[#5fa292] text-white">
            <p className="text-xs uppercase tracking-[0.25em] text-white/80">
              For mothers, fathers and daughters
            </p>
            <h2 className="font-serif text-3xl md:text-5xl mt-5 font-normal tracking-tight leading-tight">
              If your daughter is struggling,
              <br />
              <span className="italic">let's start with a real conversation.</span>
            </h2>
            <p className="mt-6 text-lg text-white/90 max-w-2xl mx-auto font-normal">
              Book a 30-minute parent consultation. We'll talk through what's
              going on, what's worth investigating, and whether working
              together is the right next step.
            </p>
            <a
              href="https://pay.gocardless.com/BRT01M17Y62WS2BX1QY0D8B0EPSXV"
              target="_blank"
              rel="noreferrer"
              data-testid="parents-cta-final"
              className="inline-block mt-10"
            >
              <Button className="bg-white text-[#5fa292] hover:bg-[#faf9f6] rounded-full px-10 h-14 text-base font-semibold">
                Book a parent consultation · £45
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
